import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, orderBy, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Wir importieren die react-beautiful-dnd Bibliothek für Drag and Drop
// npm install react-beautiful-dnd
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

function TodoView() {
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'active', 'completed'
  const [priorityFilter, setPriorityFilter] = useState('all'); // 'all', 'high', 'medium', 'low'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [newTodoPriority, setNewTodoPriority] = useState('medium');
  const [newTodoCategory, setNewTodoCategory] = useState('');
  const [categories, setCategories] = useState([
    'Entwicklung', 'Design', 'Marketing', 'Support', 'Verwaltung'
  ]);
  const [newCategory, setNewCategory] = useState('');
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  useEffect(() => {
    // Todos aus Firestore abrufen
    const q = query(collection(db, 'todos'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const todoData = snapshot.docs.map((doc, index) => ({
        id: doc.id,
        position: index,
        ...doc.data(),
        priority: doc.data().priority || 'medium',
        category: doc.data().category || ''
      }));
      setTodos(todoData);
      setLoading(false);
    }, (error) => {
      console.error("Fehler beim Laden der Todos:", error);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, []);

  const handleAddTodo = async (e) => {
    e.preventDefault();
    if (newTodo.trim() === '') return;
    
    try {
      await addDoc(collection(db, 'todos'), {
        text: newTodo,
        completed: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        priority: newTodoPriority,
        category: newTodoCategory,
        position: todos.length // Position für Drag-and-Drop
      });
      setNewTodo('');
      // Priorität und Kategorie werden nicht zurückgesetzt, um schnell mehrere Aufgaben mit gleichen Eigenschaften zu erstellen
    } catch (error) {
      console.error("Fehler beim Hinzufügen des Todos:", error);
    }
  };

  const toggleTodoStatus = async (id, currentStatus) => {
    try {
      await updateDoc(doc(db, 'todos', id), {
        completed: !currentStatus,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error("Fehler beim Ändern des Todo-Status:", error);
    }
  };

  const updateTodoPriority = async (id, newPriority) => {
    try {
      await updateDoc(doc(db, 'todos', id), {
        priority: newPriority,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error("Fehler beim Ändern der Priorität:", error);
    }
  };

  const updateTodoCategory = async (id, newCategory) => {
    try {
      await updateDoc(doc(db, 'todos', id), {
        category: newCategory,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error("Fehler beim Ändern der Kategorie:", error);
    }
  };

  const deleteTodo = async (id) => {
    try {
      await deleteDoc(doc(db, 'todos', id));
    } catch (error) {
      console.error("Fehler beim Löschen des Todos:", error);
    }
  };

  const addCategory = (e) => {
    e.preventDefault();
    if (newCategory.trim() === '' || categories.includes(newCategory)) return;
    setCategories([...categories, newCategory]);
    setNewCategory('');
    setShowCategoryForm(false);
  };

  // Drag-and-Drop Logik
  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const items = Array.from(filteredTodos);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Aktualisiere die lokale State
    const updatedTodos = todos.map(todo => {
      const found = items.find(item => item.id === todo.id);
      if (found) {
        return {...todo, position: items.indexOf(found)};
      }
      return todo;
    });
    
    setTodos(updatedTodos);
    
    // Aktualisiere die Position in der Datenbank
    try {
      await updateDoc(doc(db, 'todos', reorderedItem.id), {
        position: result.destination.index,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error("Fehler beim Aktualisieren der Position:", error);
    }
  };

  // Mehrere Filter kombinieren
  const filteredTodos = todos.filter(todo => {
    // Status-Filter
    const statusMatch = 
      filter === 'all' ? true : 
      filter === 'active' ? !todo.completed : 
      todo.completed;
    
    // Prioritäts-Filter
    const priorityMatch = 
      priorityFilter === 'all' ? true : 
      todo.priority === priorityFilter;
    
    // Kategorie-Filter
    const categoryMatch = 
      categoryFilter === 'all' ? true : 
      todo.category === categoryFilter;
    
    return statusMatch && priorityMatch && categoryMatch;
  }).sort((a, b) => a.position - b.position); // Sortieren nach Position
  
  // Anzahl der aktiven Todos
  const activeTodosCount = todos.filter(todo => !todo.completed).length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>Aufgabenliste</h2>
        <div>
          <span style={{ marginRight: '10px' }}>
            {activeTodosCount} ausstehende {activeTodosCount === 1 ? 'Aufgabe' : 'Aufgaben'}
          </span>
        </div>
      </div>

      {/* Todo-Formular mit Priorität und Kategorie */}
      <form onSubmit={handleAddTodo} style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', marginBottom: '10px' }}>
          <input
            type="text"
            value={newTodo}
            onChange={(e) => setNewTodo(e.target.value)}
            placeholder="Neue Aufgabe hinzufügen..."
            style={{
              flex: 1,
              padding: '12px 15px',
              borderRadius: '8px 0 0 8px',
              border: '1px solid #e2e8f0',
              outline: 'none',
              fontSize: '16px'
            }}
          />
          <button
            type="submit"
            style={{
              padding: '12px 20px',
              backgroundColor: '#5b21b6',
              color: 'white',
              border: 'none',
              borderRadius: '0 8px 8px 0',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Hinzufügen
          </button>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#64748b' }}>
              Priorität:
            </label>
            <select
              value={newTodoPriority}
              onChange={(e) => setNewTodoPriority(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #e2e8f0',
                outline: 'none'
              }}
            >
              <option value="high">Hoch</option>
              <option value="medium">Mittel</option>
              <option value="low">Niedrig</option>
            </select>
          </div>

          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#64748b' }}>
              Kategorie:
            </label>
            <div style={{ display: 'flex', gap: '5px' }}>
              <select
                value={newTodoCategory}
                onChange={(e) => setNewTodoCategory(e.target.value)}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #e2e8f0',
                  outline: 'none'
                }}
              >
                <option value="">Keine Kategorie</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowCategoryForm(!showCategoryForm)}
                style={{
                  padding: '8px 10px',
                  backgroundColor: '#e2e8f0',
                  color: '#4b5563',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                +
              </button>
            </div>
          </div>
        </div>

        {/* Formular für neue Kategorie */}
        {showCategoryForm && (
          <div style={{ 
            padding: '10px', 
            marginBottom: '10px', 
            backgroundColor: '#f8fafc', 
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <form onSubmit={addCategory} style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Neue Kategorie..."
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #e2e8f0',
                  outline: 'none'
                }}
              />
              <button
                type="submit"
                style={{
                  padding: '8px 15px',
                  backgroundColor: '#5b21b6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Hinzufügen
              </button>
              <button
                type="button"
                onClick={() => setShowCategoryForm(false)}
                style={{
                  padding: '8px 15px',
                  backgroundColor: '#e2e8f0',
                  color: '#4b5563',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Abbrechen
              </button>
            </form>
          </div>
        )}
      </form>

      {/* Filter-Bereich */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px', backgroundColor: '#f8fafc', padding: '15px', borderRadius: '8px' }}>
        {/* Status-Filter */}
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#64748b' }}>
            Status:
          </label>
          <div style={{ display: 'flex' }}>
            <FilterButton 
              title="Alle" 
              active={filter === 'all'} 
              onClick={() => setFilter('all')} 
            />
            <FilterButton 
              title="Aktiv" 
              active={filter === 'active'} 
              onClick={() => setFilter('active')} 
            />
            <FilterButton 
              title="Erledigt" 
              active={filter === 'completed'} 
              onClick={() => setFilter('completed')} 
            />
          </div>
        </div>

        {/* Prioritäts-Filter */}
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#64748b' }}>
            Priorität:
          </label>
          <div style={{ display: 'flex' }}>
            <FilterButton 
              title="Alle" 
              active={priorityFilter === 'all'} 
              onClick={() => setPriorityFilter('all')} 
            />
            <FilterButton 
              title="Hoch" 
              active={priorityFilter === 'high'} 
              onClick={() => setPriorityFilter('high')} 
              color="#ef4444"
            />
            <FilterButton 
              title="Mittel" 
              active={priorityFilter === 'medium'} 
              onClick={() => setPriorityFilter('medium')} 
              color="#f59e0b"
            />
            <FilterButton 
              title="Niedrig" 
              active={priorityFilter === 'low'} 
              onClick={() => setPriorityFilter('low')} 
              color="#10b981"
            />
          </div>
        </div>

        {/* Kategorie-Filter */}
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#64748b' }}>
            Kategorie:
          </label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #e2e8f0',
              outline: 'none'
            }}
          >
            <option value="all">Alle Kategorien</option>
            <option value="">Keine Kategorie</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Todo-Liste mit Drag and Drop */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
          Aufgaben werden geladen...
        </div>
      ) : filteredTodos.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '30px', 
          color: '#94a3b8',
          backgroundColor: '#f8fafc',
          borderRadius: '8px'
        }}>
          {filter === 'all' 
            ? 'Keine Aufgaben vorhanden. Fügen Sie eine neue Aufgabe hinzu!' 
            : filter === 'active' 
              ? 'Keine aktiven Aufgaben vorhanden.'
              : 'Keine erledigten Aufgaben vorhanden.'}
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="todos">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {filteredTodos.map((todo, index) => (
                  <Draggable key={todo.id} draggableId={todo.id} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <TodoItem 
                          todo={todo} 
                          onToggle={toggleTodoStatus} 
                          onDelete={deleteTodo}
                          onChangePriority={updateTodoPriority}
                          onChangeCategory={updateTodoCategory}
                          categories={categories}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
}

// Hilfkomponente: Todo-Element
function TodoItem({ todo, onToggle, onDelete, onChangePriority, onChangeCategory, categories }) {
  const [showOptions, setShowOptions] = useState(false);

  // Prioritätsfarben
  const priorityColors = {
    high: '#ef4444', // Rot
    medium: '#f59e0b', // Orange
    low: '#10b981' // Grün
  };

  // Prioritätstitel
  const priorityTitles = {
    high: 'Hoch',
    medium: 'Mittel',
    low: 'Niedrig'
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      padding: '12px 15px',
      backgroundColor: 'white',
      borderRadius: '8px',
      marginBottom: '10px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      transition: 'all 0.2s ease',
      border: '1px solid #e2e8f0',
      position: 'relative'
    }}>
      {/* Drag-Indikator */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        marginRight: '10px',
        cursor: 'grab',
        color: '#94a3b8'
      }}>
        <span>≡</span>
      </div>
      
      {/* Checkbox */}
      <div 
        onClick={() => onToggle(todo.id, todo.completed)}
        style={{
          width: '22px',
          height: '22px',
          borderRadius: '50%',
          border: todo.completed ? '2px solid #5b21b6' : '2px solid #cbd5e1',
          backgroundColor: todo.completed ? '#5b21b6' : 'transparent',
          marginRight: '15px',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          fontSize: '12px'
        }}
      >
        {todo.completed && '✓'}
      </div>
      
      {/* Prioritäts-Indikator */}
      <div 
        style={{
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          backgroundColor: priorityColors[todo.priority],
          marginRight: '15px'
        }}
        title={`Priorität: ${priorityTitles[todo.priority]}`}
      />
      
      {/* Text */}
      <span style={{
        flex: 1,
        textDecoration: todo.completed ? 'line-through' : 'none',
        color: todo.completed ? '#94a3b8' : '#1e293b',
        transition: 'all 0.2s ease'
      }}>
        {todo.text}
        {todo.category && (
          <span style={{
            marginLeft: '10px',
            fontSize: '12px',
            padding: '2px 8px',
            backgroundColor: '#f1f5f9',
            borderRadius: '12px',
            color: '#64748b'
          }}>
            {todo.category}
          </span>
        )}
      </span>
      
      {/* Options Button */}
      <button
        onClick={() => setShowOptions(!showOptions)}
        style={{
          background: 'none',
          border: 'none',
          color: '#94a3b8',
          cursor: 'pointer',
          fontSize: '18px',
          padding: '5px',
          borderRadius: '5px',
          marginRight: '5px'
        }}
      >
        ⋯
      </button>
      
      {/* Delete Button */}
      <button
        onClick={() => onDelete(todo.id)}
        style={{
          background: 'none',
          border: 'none',
          color: '#94a3b8',
          cursor: 'pointer',
          fontSize: '18px',
          padding: '5px',
          borderRadius: '5px'
        }}
      >
        🗑️
      </button>
      
      {/* Options Popup */}
      {showOptions && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: '0',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0',
          zIndex: 10,
          padding: '10px',
          minWidth: '200px'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#64748b' }}>
              Priorität ändern:
            </label>
            <div style={{ display: 'flex', gap: '5px' }}>
              <button
                onClick={() => {
                  onChangePriority(todo.id, 'high');
                  setShowOptions(false);
                }}
                style={{
                  flex: 1,
                  padding: '5px',
                  backgroundColor: todo.priority === 'high' ? '#fecaca' : '#f1f5f9',
                  color: '#ef4444',
                  border: todo.priority === 'high' ? '1px solid #ef4444' : '1px solid #e2e8f0',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Hoch
              </button>
              <button
                onClick={() => {
                  onChangePriority(todo.id, 'medium');
                  setShowOptions(false);
                }}
                style={{
                  flex: 1,
                  padding: '5px',
                  backgroundColor: todo.priority === 'medium' ? '#fef3c7' : '#f1f5f9',
                  color: '#f59e0b',
                  border: todo.priority === 'medium' ? '1px solid #f59e0b' : '1px solid #e2e8f0',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Mittel
              </button>
              <button
                onClick={() => {
                  onChangePriority(todo.id, 'low');
                  setShowOptions(false);
                }}
                style={{
                  flex: 1,
                  padding: '5px',
                  backgroundColor: todo.priority === 'low' ? '#d1fae5' : '#f1f5f9',
                  color: '#10b981',
                  border: todo.priority === 'low' ? '1px solid #10b981' : '1px solid #e2e8f0',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Niedrig
              </button>
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: '#64748b' }}>
              Kategorie ändern:
            </label>
            <select
              value={todo.category}
              onChange={(e) => {
                onChangeCategory(todo.id, e.target.value);
                setShowOptions(false);
              }}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #e2e8f0',
                outline: 'none'
              }}
            >
              <option value="">Keine Kategorie</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

// Filter-Button Komponente
function FilterButton({ title, active, onClick, color }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 16px',
        margin: '0 5px',
        backgroundColor: active ? (color ? `${color}20` : '#f0eafb') : 'transparent',
        color: active ? (color || '#5b21b6') : '#64748b',
        border: '1px solid',
        borderColor: active ? (color || '#5b21b6') : '#e2e8f0',
        borderRadius: '30px',
        cursor: 'pointer',
        fontWeight: active ? 'bold' : 'normal',
        transition: 'all 0.2s ease'
      }}
    >
      {title}
    </button>
  );
}

export default TodoView;